import React, { useState, useMemo } from 'react';
import { csvCell } from '../../lib/csvCell';
import { confirmDialog } from '../../lib/confirmStore';
import { isApplicationActive } from '../../lib/constants';
import { usePrograms } from '../../hooks/usePrograms';
import { getPipelineStages, stageLabel as getStageLabel } from '../../lib/programPipeline';
import { Search, Download, RefreshCw, ArrowLeft, Archive, Eye, CheckCircle2, UserCheck } from 'lucide-react';

interface ArchivedApplicationsProps {
  applications: any[];
  onRestoreApplicant: (appId: string) => Promise<void>;
  onInspectApplicant: (app: any) => void;
  onBackToActive: () => void;
}

export const ArchivedApplications: React.FC<ArchivedApplicationsProps> = ({
  applications,
  onRestoreApplicant,
  onInspectApplicant,
  onBackToActive,
}) => {
  const [search, setSearch] = useState('');
  const [restoringId, setRestoringId] = useState<string | null>(null);

  // Filter out active applications -> keep only archived / cancelled / closed
  const archivedApps = useMemo(() => {
    return (applications || []).filter(app => !isApplicationActive(app));
  }, [applications]);

  // Apply search query
  const filteredArchivedApps = useMemo(() => {
    if (!search.trim()) return archivedApps;
    const term = search.toLowerCase();
    return archivedApps.filter(a => {
      const name = `${a.personalDetails?.firstName || ''} ${a.personalDetails?.lastName || ''}`.toLowerCase();
      const email = (a.userEmail || a.personalDetails?.email || '').toLowerCase();
      const appId = (a.applicationId || '').toLowerCase();
      const prog = (a.programType || a.programId || '').toLowerCase();
      return name.includes(term) || email.includes(term) || appId.includes(term) || prog.includes(term);
    });
  }, [archivedApps, search]);

  // Export archived list to CSV
  const { programs } = usePrograms();

  const handleExportCsv = () => {
    if (filteredArchivedApps.length === 0) return;
    const headers = ["Application ID", "Candidate Name", "Email", "Program", "Status", "Reason", "Archived Date", "Last Stage"];
    const rows = filteredArchivedApps.map(a => {
      const matchedProg = (programs || []).find((x: any) => x.id === a.programId || x.programId === a.programId || x.name === a.programType || x.code === a.programType);
      const stages = getPipelineStages(matchedProg);
      const stageLabelStr = getStageLabel(stages, a.masterStage, 'N/A');
      return [
        a.applicationId || '',
        `${(a.personalDetails?.firstName || '')} ${(a.personalDetails?.lastName || '')}`,
        a.userEmail || a.personalDetails?.email || '',
        a.programType || a.programId || '',
        a.status || a.submissionStatus || 'Archived',
        a.returnNote || a.closureReason || 'N/A',
        a.updatedAt || a.createdAt || '',
        stageLabelStr,
      ];
    });

    const csvContent = "data:text/csv;charset=utf-8," + [headers.map(csvCell).join(","), ...rows.map(e => e.map(csvCell).join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `archived_applications_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRestore = async (appId: string) => {
    if (!(await confirmDialog("Are you sure you want to restore this application back to active status?"))) return;
    try {
      setRestoringId(appId);
      await onRestoreApplicant(appId);
    } catch (err) {
      console.error("Restore failed:", err);
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Navigation Header */}
      <div className="bg-white border border-slate-200/80 rounded-2xl p-5 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBackToActive}
            className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition cursor-pointer"
            title="Back to Active Programs"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <Archive className="w-5 h-5 text-slate-600" />
              <h2 className="text-lg font-black text-slate-900 tracking-tight">Archived Applications</h2>
              <span className="px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-700 font-extrabold text-xs border border-slate-200">
                {archivedApps.length}
              </span>
            </div>
            <p className="text-xs text-slate-500 font-medium mt-0.5">
              Review, audit, search, export, or reactivate cancelled and closed candidates.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={filteredArchivedApps.length === 0}
            className="px-3.5 py-2 bg-brand hover:bg-brand-active text-white font-bold text-xs rounded-xl shadow-xs transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            <span>Export CSV ({filteredArchivedApps.length})</span>
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white border border-slate-200/80 rounded-2xl p-3 shadow-2xs">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by candidate name, email, application ID, or program..."
            className="w-full pl-10 pr-4 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand font-medium"
          />
        </div>
      </div>

      {/* Archived Data Table */}
      <div className="bg-white border border-slate-200/90 rounded-2xl shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/80 border-b border-slate-200 text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">
                <th className="py-3 px-4">Candidate</th>
                <th className="py-3 px-4">Program</th>
                <th className="py-3 px-4">Primary Reason / Note</th>
                <th className="py-3 px-4">Closed By</th>
                <th className="py-3 px-4">Date Archived</th>
                <th className="py-3 px-4">Last Stage</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs font-medium text-slate-700">
              {filteredArchivedApps.map((app) => {
                const fullName = `${app.personalDetails?.firstName || ''} ${app.personalDetails?.lastName || ''}`.trim() || 'Unnamed Candidate';
                const email = app.userEmail || app.personalDetails?.email || 'No email';
                const prog = app.programType || app.programId || 'Unspecified Program';
                const reason = app.returnNote || app.closureReason || app.statusNote || 'No reason recorded';
                const closedBy = app.closedBy || app.adminNotesModifiedBy || 'System / Admin';
                const archivedDate = app.updatedAt ? new Date(app.updatedAt).toLocaleDateString() : 'N/A';
                const isRestoring = restoringId === app.applicationId;

                return (
                  <tr key={app.applicationId} className="hover:bg-slate-50/60 transition-colors">
                    <td className="py-3 px-4">
                      <div className="font-extrabold text-slate-900">{fullName}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{app.applicationId} • {email}</div>
                    </td>
                    <td className="py-3 px-4 font-semibold text-slate-800">
                      {prog}
                    </td>
                    <td className="py-3 px-4 max-w-xs truncate text-slate-500" title={reason}>
                      {reason}
                    </td>
                    <td className="py-3 px-4 text-slate-600 font-medium">
                      {closedBy}
                    </td>
                    <td className="py-3 px-4 text-slate-500 font-mono text-[11px]">
                      {archivedDate}
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-bold text-[10px]">
                        {(() => {
                          const matchedProg = (programs || []).find((x: any) => x.id === app.programId || x.programId === app.programId || x.name === app.programType || x.code === app.programType);
                          const stages = getPipelineStages(matchedProg);
                          return getStageLabel(stages, app.masterStage, 'N/A');
                        })()}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right space-x-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => onInspectApplicant(app)}
                        className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg text-[11px] inline-flex items-center gap-1 transition cursor-pointer"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        <span>Inspect</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRestore(app.applicationId)}
                        disabled={isRestoring}
                        className="px-2.5 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 font-bold rounded-lg text-[11px] inline-flex items-center gap-1 transition cursor-pointer disabled:opacity-50"
                      >
                        {isRestoring ? (
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <UserCheck className="w-3.5 h-3.5" />
                        )}
                        <span>Restore</span>
                      </button>
                    </td>
                  </tr>
                );
              })}

              {filteredArchivedApps.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-slate-400 font-medium">
                    No archived applications found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ArchivedApplications;
